import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Globe, ChevronDown, ChevronUp, LogOut, Bell, UserPlus, Trash2 } from 'lucide-react';

const allLanguages = [
  { value: 'english', label: 'English' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'hinglish', label: 'Hinglish' },
  { value: 'spanish', label: 'Español (Spanish)' },
  { value: 'french', label: 'Français (French)' },
  { value: 'arabic', label: 'العربية (Arabic)' },
  { value: 'chinese', label: '中文 (Chinese)' },
  { value: 'portuguese', label: 'Português (Portuguese)' },
  { value: 'japanese', label: '日本語 (Japanese)' },
  { value: 'german', label: 'Deutsch (German)' },
  { value: 'russian', label: 'Русский (Russian)' },
  { value: 'korean', label: '한국어 (Korean)' },
  { value: 'indonesian', label: 'Bahasa Indonesia' },
  { value: 'turkish', label: 'Türkçe (Turkish)' },
];

const ToggleRow = ({ icon: Icon, title, subtitle, checked, onChange, testId }) => (
  <div className="glass-panel rounded-[16px] p-4 flex items-center justify-between gap-3">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center border border-white/90">
        <Icon className="w-4 h-4 text-[#9333EA]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#000000] font-['Outfit']">{title}</p>
        <p className="text-xs text-[#666666] font-['DM_Sans']">{subtitle}</p>
      </div>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#9333EA] transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
    </label>
  </div>
);

const SettingsModal = ({ isOpen, onClose }) => {
  const { language, setLanguage, logout, user, updateNotificationSettings, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [languageOpen, setLanguageOpen] = useState(false);
  const [referralNotificationsEnabled, setReferralNotificationsEnabled] = useState(true);
  const [winnerAnnouncementNotificationsEnabled, setWinnerAnnouncementNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    setReferralNotificationsEnabled(user?.referralNotificationsEnabled ?? true);
    setWinnerAnnouncementNotificationsEnabled(user?.winnerAnnouncementNotificationsEnabled ?? true);
    if (!isOpen) {
      setShowDeleteConfirm(false);
      setDeleteError('');
      setDeleting(false);
    }
  }, [user, isOpen]);

  const currentLangLabel = allLanguages.find((l) => l.value === language)?.label || 'English';

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError('');
    const result = await deleteAccount();
    if (!result?.success) {
      setDeleteError(result?.error || 'Failed to delete account. Please try again.');
      setDeleting(false);
      return;
    }
    // Account deleted + local session cleared. Close modal and redirect to login.
    setShowDeleteConfirm(false);
    setDeleting(false);
    onClose();
    navigate('/login', { replace: true });
  };

  const handleToggleSave = async (key, value) => {
    setSaving(true);

    const nextPayload = {
      referralNotificationsEnabled: key === 'referral' ? value : referralNotificationsEnabled,
      winnerAnnouncementNotificationsEnabled: key === 'winner' ? value : winnerAnnouncementNotificationsEnabled,
    };

    const result = await updateNotificationSettings(nextPayload);
    if (!result.success) {
      // rollback UI state when API fails
      setReferralNotificationsEnabled(user?.referralNotificationsEnabled ?? true);
      setWinnerAnnouncementNotificationsEnabled(user?.winnerAnnouncementNotificationsEnabled ?? true);
    }

    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md glass-panel border-white/40 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-2xl text-[#000000]">
            Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Language Section - Collapsible */}
          <div className="glass-panel rounded-[16px] overflow-hidden">
            <button
              onClick={() => setLanguageOpen(!languageOpen)}
              data-testid="language-section-toggle"
              className="w-full flex items-center justify-between p-4"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-[#9333EA]" />
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-[#000000] font-['Outfit']">
                    Language
                  </h4>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">
                    {currentLangLabel}
                  </p>
                </div>
              </div>
              {languageOpen ? (
                <ChevronUp className="w-5 h-5 text-[#666666]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#666666]" />
              )}
            </button>

            {languageOpen && (
              <div className="px-4 pb-4 space-y-1.5 max-h-[300px] overflow-y-auto">
                {allLanguages.map((lang) => (
                  <button
                    key={lang.value}
                    onClick={() => {
                      setLanguage(lang.value);
                      setLanguageOpen(false);
                    }}
                    data-testid={`language-option-${lang.value}`}
                    className={`w-full p-3 rounded-[12px] text-left text-sm font-['DM_Sans'] transition-colors ${
                      language === lang.value
                        ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold'
                        : 'bg-white/50 text-[#000000] hover:bg-white/70'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notification Settings */}
          <div className="space-y-3">
            <ToggleRow
              icon={UserPlus}
              title="Referral notifications"
              subtitle="Phone notifications for new referral joins (outside app)"
              checked={referralNotificationsEnabled}
              onChange={(checked) => {
                setReferralNotificationsEnabled(checked);
                handleToggleSave('referral', checked);
              }}
              testId="referral-notifications-toggle"
            />

            <ToggleRow
              icon={Bell}
              title="7 PM notifications"
              subtitle="Phone notifications for 7 PM celebrations (outside app)"
              checked={winnerAnnouncementNotificationsEnabled}
              onChange={(checked) => {
                setWinnerAnnouncementNotificationsEnabled(checked);
                handleToggleSave('winner', checked);
              }}
              testId="winner-announcement-toggle"
            />

            {saving && (
              <p className="text-xs text-[#666666] font-['DM_Sans'] px-1">Saving notification preferences...</p>
            )}
          </div>

          {/* About */}
          <div className="glass-panel rounded-[16px] p-5 sm:p-6">
            <h4 className="text-sm font-semibold text-[#000000] mb-3 font-['Outfit'] text-center">
              About Harday
            </h4>
            <div className="text-center font-['DM_Sans'] text-[15px] sm:text-base text-[#333333] leading-relaxed tracking-[0.01em] space-y-1.5">
              <p className="italic">
                You don&rsquo;t take anything seriously&hellip; until you pay for it.
              </p>
              <p>
                This is what puzzles can do for you &mdash; and how winning can entertain your life.
              </p>
              <p className="font-semibold text-[#000000]">
                This is Harday.
              </p>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full glass-panel rounded-[16px] p-4 flex items-center gap-3 hover:bg-red-50/50 transition-colors"
          >
            <LogOut className="w-5 h-5 text-red-500" />
            <span className="text-sm font-semibold text-red-500 font-['Outfit']">
              Logout
            </span>
          </button>

          {/* Delete Account — placed at the bottom, subtle warning style */}
          <div className="pt-1">
            <button
              onClick={() => {
                setDeleteError('');
                setShowDeleteConfirm(true);
              }}
              data-testid="delete-account-btn"
              className="w-full glass-panel rounded-[16px] p-3.5 flex items-center justify-center gap-2 hover:bg-red-50/40 transition-colors border border-red-100"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
              <span className="text-xs font-semibold text-red-400 font-['Outfit']">
                Delete Account
              </span>
            </button>
            <p className="mt-2 text-[11px] text-[#888] font-['DM_Sans'] text-center">
              Permanently delete your account and all data.
            </p>
          </div>

          {/* Version */}
          <div className="text-center">
            <p className="text-xs text-[#666666] font-['DM_Sans']">
              Version 1.0.0
            </p>
          </div>
        </div>

        {/* Delete confirmation popup (rendered as an in-app overlay above the
            Settings dialog content). */}
        {showDeleteConfirm && (
          <div
            className="fixed inset-0 z-[260] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              if (!deleting) setShowDeleteConfirm(false);
            }}
          >
            <div
              className="bg-white rounded-[20px] shadow-2xl w-full max-w-sm p-5"
              onClick={(e) => e.stopPropagation()}
              data-testid="delete-account-confirm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-[#000000] font-['Outfit']">
                  Delete Account
                </h3>
              </div>
              <p className="text-sm text-[#444] font-['DM_Sans'] mb-4">
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
              {deleteError && (
                <p className="mb-3 rounded-[12px] bg-red-50 px-3 py-2 text-xs font-['DM_Sans'] text-red-600">
                  {deleteError}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  data-testid="delete-account-cancel"
                  className="flex-1 glass-panel rounded-full py-2.5 text-sm font-semibold text-[#000000] font-['Outfit'] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  data-testid="delete-account-confirm-btn"
                  className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white font-['Outfit'] bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
